'use client';

import { useBunja } from 'bunja/react';
import { useAtomValue } from 'jotai';
import { useSearchParams } from 'next/navigation';
import { ComponentProps, MouseEvent, ReactNode, Suspense } from 'react';

import { authBunja } from '@flipster/features/src/shared/auth/state';
import { useIsWebview } from '@flipster/features/src/shared/webview';
import { FormattedMessage } from '@flipster/i18n';
import { useOverlay } from '@flipster/overlay';
import { Button } from '@flipster/react/design-system/button';
import { IconButton } from '@flipster/react/design-system/icon-button';
import QrCodeIcon from '@flipster/react/icon/generated/QrCodeIcon';
import { useMounted } from '@flipster/react/misc/lifecycle/mount';
import { hstack } from '@flipster/styled-system/patterns';
import segment, { marketingEventEvent } from '@flipster/utils/segment';
import { generateLinkWithQueryParams } from '@flipster/utils/url';
import webviewHandler from '@flipster/utils/webview-handler';

import { AppDownloadModal } from '@web/app/[locale]/(web)/_app-download/modal';

interface Props extends Pick<ComponentProps<typeof Button>, 'hierarchy' | 'color' | 'size' | 'css'> {
  label?: ReactNode;
  buttonText?: string;
  path: string;
  locator?: string;
  logger?: (e: MouseEvent<HTMLAnchorElement>) => void;
}

const BaseButton = ({ label, path, locator, logger, ...buttonProps }: Props) => {
  const isWebview = useIsWebview();
  const searchParams = useSearchParams();

  const isExternalLink = path.startsWith('https://');

  return (
    <Button
      as='a'
      label={label}
      href={generateLinkWithQueryParams(path, searchParams)}
      newTab={isExternalLink}
      onClick={(e) => {
        logger?.(e);
        if (isWebview && locator != null) {
          e.preventDefault();
          webviewHandler.postMessage({
            action: 'link',
            params: { page: locator },
          });
        }
      }}
      {...buttonProps}
    />
  );
};

export const LinkOrWebviewHandler = (props: Props) => {
  return (
    <Suspense>
      <BaseButton {...props} />
    </Suspense>
  );
};

export const CampaignButton = ({ label, buttonText, path, locator }: Props) => {
  const overlay = useOverlay();

  const handleLogging = (e: MouseEvent<HTMLElement> | MouseEvent<HTMLAnchorElement>) => {
    if (buttonText == null || path == null) return;
    const sections = Array.from(document.querySelectorAll<HTMLElement>('main > section'));
    const sectionIndex = sections.findIndex((section) => section.contains(e.currentTarget as HTMLElement));
    if (sectionIndex === -1) return;
    const currentSection = sections[sectionIndex];
    if (!currentSection) return;
    const sectionType = currentSection.dataset.sectionType;
    if (sectionType == null) return;

    segment.track(marketingEventEvent.buttonClick, {
      section_type: sectionType,
      section_index: sectionIndex,
      button_text: buttonText,
      button_url: path,
    });
  };

  if (path === '?action=download-app') {
    return (
      <div className={hstack({ textStyle: 'Text/M/Strong', color: 'content.default.level-3', gap: 16 })}>
        <span>Download app</span>
        <IconButton
          hierarchy='tertiary'
          size='large'
          icon={<QrCodeIcon />}
          onClick={(e) => {
            handleLogging(e as MouseEvent<HTMLElement>);
            overlay.open(({ isOpen, onClose, unmount }) => (
              <AppDownloadModal isOpen={isOpen} onClose={onClose} onExit={unmount} />
            ));
          }}
        />
      </div>
    );
  }

  return (
    <LinkOrWebviewHandler
      hierarchy='primary'
      color='accent'
      size='large'
      label={label}
      path={path}
      locator={locator}
      logger={handleLogging}
    />
  );
};

interface WebAppJsonButtonProps {
  json?: Record<'web' | 'app', Record<'notLoggedIn' | 'loggedIn', { text: string; url: string }>>;
}

const BaseWebAppJsonButton = ({ json, ...props }: WebAppJsonButtonProps) => {
  const isWebview = useIsWebview();
  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);
  const isMounted = useMounted();

  const statusText = isMounted && isLoggedIn ? 'loggedIn' : 'notLoggedIn';
  const appJson = json?.app[statusText];
  const webJson = json?.web[statusText];

  if (!webJson?.url) return null;
  return (
    <CampaignButton
      label={isWebview ? appJson?.text : webJson?.text}
      path={webJson.url}
      locator={appJson?.url}
      {...props}
    />
  );
};

export const WebAppJsonButton = (props: WebAppJsonButtonProps) => {
  return (
    <Suspense>
      <BaseWebAppJsonButton {...props} />
    </Suspense>
  );
};

interface TopButtonProps {
  isFinished?: boolean;
  isRegistered?: boolean;
  handleRegister?: () => void;
}

export const RegisterButton = ({ isFinished, isRegistered, handleRegister }: TopButtonProps) => {
  const { isLoggedInAtom } = useBunja(authBunja);
  const isLoggedIn = useAtomValue(isLoggedInAtom);

  if (!isFinished && !isLoggedIn)
    return (
      <CampaignButton
        label={<FormattedMessage id='Common.r251124.Sign_up_now' />}
        buttonText='Sign_up_now'
        path='/signup'
        locator='signup'
      />
    );
  if (!isFinished && !isRegistered)
    return (
      <Button
        hierarchy='primary'
        color='accent'
        size='large'
        label={<FormattedMessage id='Common.r250923.Register_now' />}
        onClick={handleRegister}
      />
    );
  return (
    <CampaignButton
      label={<FormattedMessage id='Common.r251104.Trade_now' />}
      buttonText='Trade_now'
      path='/trade/perpetual/latest'
      locator='/trade/latest'
    />
  );
};
